import { getRecordActionItems } from '@/features/RecordActions/model/recordActionItems';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useOrderProblems } from '@/entities/OrderProblem/model/store';
import {
    formatDiagnosticPayload,
    getOrderProblemDetails,
} from '@/entities/OrderProblem/config/order-problem.config';
import { getMainOrderDetailsData, getAdditionalOrderDetailsData } from '@/entities/Order/config/order-details.config';
import { PaymentDetails } from '@/entities/Payment/ui/PaymentDetails';
import EventsTable from '@/entities/Event/ui/EventsTable';
import { AddressView } from '@/entities/Address/ui/AddressView';
import { RequestStatus } from '@/shared/model/requestStatus';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { ActionMenu } from '@/shared/ui/ActionMenu/ActionMenu';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { FormDetails } from '@/shared/ui/FormDetails/FormDetails';
import { Section } from '@/shared/ui/Section/Section';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { formatDateTime } from '@/core/format';
import buttonClasses from '@/styles/buttons.module.scss';
import classes from './OrderProblemsPage.module.scss';

/** Отображает проблему, исходный запрос оплаты и связанные данные заказа */
export const OrderProblemDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const details = useOrderProblems((state) => state.current);
    const status = useOrderProblems((state) => state.detailsStatus);
    const error = useOrderProblems((state) => state.detailsError);
    const mutationStatus = useOrderProblems((state) => state.mutationStatus);
    const mutationError = useOrderProblems((state) => state.mutationError);
    const fetchDetails = useOrderProblems((state) => state.fetchDetails);
    const retry = useOrderProblems((state) => state.retry);
    const resolve = useOrderProblems((state) => state.resolve);

    useEffect(() => {
        if (id) void fetchDetails(id);
    }, [id, fetchDetails]);

    if (!id) return <RequestEmpty message="Не указан идентификатор проблемы" />;

    if (status === RequestStatus.Error) {
        return <RequestError
            message={error ?? 'Не удалось загрузить проблему заказа'}
            onRetry={() => fetchDetails(id)}
        />;
    }

    const waitingForDetails = status === RequestStatus.Idle || status === RequestStatus.Loading || (details?.problem && details.problem.id !== id);

    if (waitingForDetails) {
        return <RequestLoading message="Загрузка диагностики заказа..." />;
    }

    if (!details?.problem) return <RequestEmpty message="Проблема заказа не найдена" />;

    const { problem, order } = details;
    const busy = mutationStatus === RequestStatus.Loading;

    /** Повторяет обработку исходного уведомления */
    const repeat = async () => {
        if (!await retry(id)) return;

        const updated = useOrderProblems.getState().current?.problem;

        if (updated?.isResolved) toast.success('Проблема устранена');
        else toast.info('Обработка повторена. Проблема пока не устранена');
    };

    /** Отмечает проблему операции решённой */
    const markResolved = async () => {
        if (await resolve(id)) toast.success('Проблема отмечена решённой');
    };

    const actions = getRecordActionItems({
        kind: 'orderProblems',
        record: problem
    }, {
        resolve: () => void markResolved()
    });

    return <div className={classes.page}>
        <EntityActionPanel
            name={problem.isResolved
                ? 'Проблема устранена'
                : '! Проблемный заказ'
            }
            variant={problem.isResolved
                ? 'success'
                : 'warning'
            }
        >
            <div className={buttonClasses.buttonGroup}>
                <BackButton url="/order-problems" />
                {problem.canRetry && (
                    <ActionButton
                        label="Повторить обработку"
                        icon="pi pi-refresh"
                        permission="admin.order-problems.retry"
                        loading={busy}
                        onClick={() => void repeat()}
                    />
                )}
                <ActionMenu
                    model={actions}
                    disabled={busy}
                    aria-label="Действия с проблемой"
                />
            </div>
        </EntityActionPanel>
        {mutationError && (
            <RequestError message={mutationError} />
        )}
        <Section title="Диагностика проблемы">
            <FormDetails dataForShow={getOrderProblemDetails(problem)} />
        </Section>
        {details.providerPayload && (
            <Section title="Исходный запрос Robokassa">
                <p>Сохранены все поля исходного уведомления. Повторная обработка использует эти данные.</p>
                <pre className={classes.payload}>{formatDiagnosticPayload(details.providerPayload)}</pre>
            </Section>
        )}
        {problem.diagnosticData && (
            <Section title="Параметры операции">
                <pre className={classes.payload}>{formatDiagnosticPayload(problem.diagnosticData)}</pre>
            </Section>
        )}
        <Section title="История ошибок">
            <ol className={classes.history}>
                {(details.history ?? []).map((attempt) => <li key={attempt.attemptNumber}>
                    <strong>Попытка {attempt.attemptNumber} — {formatDateTime(attempt.occurredAt)}</strong>
                    <p>{attempt.errorMessage} ({attempt.errorCode})</p>
                </li>)}
            </ol>
        </Section>
        {order
            ? <>
                {details.isOrderSnapshot && (
                    <p className={classes.notice}>Показаны сохранённые данные заказа на момент ошибки.</p>
                )}
                <Section title={`Заказ №${order.orderNumber ?? ''}`}>
                    <FormDetails dataForShow={getMainOrderDetailsData(order)} />
                    <AddressView address={order.orderAddress} />
                    {problem.orderId && (
                        <ActionButton
                            label="Открыть карточку заказа"
                            icon="pi pi-external-link"
                            onClick={() => window.open(`/orders/${problem.orderId}`, '_blank', 'noopener,noreferrer')}
                        />
                    )}
                </Section>
                <Section title="Дополнительные данные заказа">
                    <FormDetails dataForShow={getAdditionalOrderDetailsData(order)} />
                </Section>
                {order.payment && (
                    <Section title="Платёж">
                        <PaymentDetails
                            payment={order.payment}
                            orderProducts={order.orderProducts ?? []}
                        />
                    </Section>
                )}
                {(order.events?.length ?? 0) > 0 && (
                    <Section title="События заказа">
                        <EventsTable
                            events={order.events ?? []}
                            embedded
                        />
                    </Section>
                )}
                <Section title="Полные данные заказа">
                    <pre className={classes.payload}>{JSON.stringify(order, null, 2)}</pre>
                </Section>
            </>
            : <RequestEmpty message="Данные заказа не найдены в базе. Доступная информация и исходный запрос сохранены выше" />}
    </div>;
};
